// components/edit_portfolio_fees.tsx
import React, { useState, useContext, useCallback } from 'react';
import { NumberInput, Button, Alert, TextInput } from '@mantine/core';
import { DocumentReference, updateDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { MyUserDataContext } from '../contexts';
import { Service, UpdatedDataType } from '../types';

interface EditPortfolioFeesProps {
    serviceRef: DocumentReference;
    service: Service;
}

interface ValidationErrors {
    fees?: string;
    reason?: string;
}

// Validation constants
const VALIDATION_RULES = {
    MIN_REASON_LENGTH: 10,
} as const;

const EditPortfolioFees: React.FC<EditPortfolioFeesProps> = ({ serviceRef, service }) => {
    const [updating, setUpdating] = useState(false);
    const [fees, setFees] = useState<number | undefined>(service.portfolioOpeningRequest?.fees);
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<ValidationErrors>({});

    const userData = useContext(MyUserDataContext);

    // Validation helpers
    const validateFees = useCallback((value: number | undefined): string | undefined => {
        if (value === undefined || value === null) return "Portfolio Fees is required";
        if (value < 0) return "Fees cannot be negative";
        return undefined;
    }, []);

    const validateReason = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return "Reason is required";
        if (trimmed.length < VALIDATION_RULES.MIN_REASON_LENGTH) {
            return `Reason must be at least ${VALIDATION_RULES.MIN_REASON_LENGTH} characters`;
        }
        return undefined;
    }, []);

    const validateForm = useCallback((): boolean => {
        const feesError = validateFees(fees);
        const reasonError = validateReason(reason);

        const newErrors: ValidationErrors = {
            fees: feesError,
            reason: reasonError,
        };

        setErrors(newErrors);
        return !feesError && !reasonError;
    }, [fees, reason, validateFees, validateReason]);

    const isFormValid = useCallback((): boolean => {
        return fees !== undefined &&
            fees >= 0 &&
            reason.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH;
    }, [fees, reason]);

    // Event handlers
    const handleFeesChange = useCallback((value: string | number) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        setFees(numValue);

        // Clear error if input becomes valid
        if (numValue !== undefined && numValue >= 0 && errors.fees) {
            setErrors(prev => ({ ...prev, fees: undefined }));
        }
    }, [errors.fees]);

    const handleFeesBlur = useCallback(() => {
        const error = validateFees(fees);
        if (error) {
            setErrors(prev => ({ ...prev, fees: error }));
        }
    }, [fees, validateFees]);

    const handleReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setReason(value);

        // Clear error if input becomes valid
        if (value.trim().length >= VALIDATION_RULES.MIN_REASON_LENGTH && errors.reason) {
            setErrors(prev => ({ ...prev, reason: undefined }));
        }
    }, [errors.reason]);

    const handleReasonBlur = useCallback(() => {
        const error = validateReason(reason);
        if (error) {
            setErrors(prev => ({ ...prev, reason: error }));
        }
    }, [reason, validateReason]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;

        setUpdating(true);

        try {
            const newEntry: UpdatedDataType = {
                updated_by: userData.user?.email || "unknown",
                updated_on: Timestamp.now(),
                updated_field_name: ["fees"],
                updated_fields: [{ fees: fees || 0 }],
                initial_value: [{ fees: service.portfolioOpeningRequest?.fees || 0 }],
                reason: reason.trim()
            };

            const portfolioOpeningRequest = {
                ...service.portfolioOpeningRequest,
                fees: fees || 0
            };

            await updateDoc(serviceRef, {
                portfolioOpeningRequest,
                updated_data: arrayUnion(newEntry)
            });

            // Show success notification
            notifications.show({
                title: 'Success!',
                message: 'Portfolio Fees updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });

            // Reset form and close modal
            setFees(undefined);
            setReason('');
            setErrors({});
            modals.closeAll();

        } catch (error) {
            console.error("Error updating portfolio fees:", error);

            // Show error notification
            const message = error instanceof Error ? error.message : 'Unknown error occurred';
            notifications.show({
                title: 'Error',
                message: `Failed to update portfolio fees: ${message}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setUpdating(false);
        }
    }, [validateForm, userData.user?.email, fees, reason, service.portfolioOpeningRequest, serviceRef]);

    // Derived state
    const hasErrors = Boolean(errors.fees || errors.reason);
    const formStatus = isFormValid();

    return (
        <>
            <NumberInput
                label="Portfolio Fees"
                data-autofocus
                placeholder="Enter portfolio fees"
                value={fees}
                onChange={handleFeesChange}
                onBlur={handleFeesBlur}
                min={0}
                decimalScale={2}
                error={errors.fees}
                disabled={updating}
                required
            />

            <TextInput
                label="Reason for Change"
                value={reason}
                onChange={handleReasonChange}
                onBlur={handleReasonBlur}
                error={errors.reason}
                placeholder={`Please provide a detailed reason (minimum ${VALIDATION_RULES.MIN_REASON_LENGTH} characters)...`}
                disabled={updating}
                required
            />

            <Button
                loading={updating}
                fullWidth
                disabled={updating}
                onClick={handleSubmit}
                mt="md"
            >
                Submit
            </Button>

            {/* Enhanced validation summary */}
            {hasErrors && (
                <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Please fix the following errors:"
                    color="red"
                    mt="sm"
                >
                    {errors.fees && <div>• {errors.fees}</div>}
                    {errors.reason && <div>• {errors.reason}</div>}
                </Alert>
            )}

            {/* Form status indicator - don't show during loading */}
            {!updating && (
                <Alert
                    color={formStatus ? 'green' : 'gray'}
                    variant="light"
                    mt="xs"
                    style={{ fontSize: '12px' }}
                >
                    Form is {formStatus ? 'ready to submit' : 'incomplete'}
                </Alert>
            )}
        </>
    );
};

export default EditPortfolioFees;